import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Pencil } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectUserProfile } from "../../features/authentication/authenticationSlice";
import { updateProfileNameAsync } from "../../features/authentication/authenticationApi";

const getInitials = (name?: string): string => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

/** The signed-in account row on Settings, with inline first/last name editing. */
const ProfileNameEditor = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);

  const startEditing = () => {
    const parts = (userProfile?.name ?? "").trim().split(" ");
    setFirstName(parts[0] ?? "");
    setLastName(parts.slice(1).join(" "));
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await dispatch(
        updateProfileNameAsync({ firstName: firstName.trim(), lastName: lastName.trim() }),
      ).unwrap();
      toast.success(t("settings.profileName.saved", "Name updated."));
      setEditing(false);
    } catch (error) {
      toast.error(
        (error as { message?: string })?.message ||
          t("settings.profileName.failed", "Couldn't update the name. Please try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <input
          className="input"
          value={firstName}
          maxLength={100}
          onChange={(event) => setFirstName(event.target.value)}
          placeholder={t("settings.profileName.firstName", "First name")}
          aria-label={t("settings.profileName.firstName", "First name")}
        />
        <input
          className="input"
          value={lastName}
          maxLength={100}
          onChange={(event) => setLastName(event.target.value)}
          placeholder={t("settings.profileName.lastName", "Last name")}
          aria-label={t("settings.profileName.lastName", "Last name")}
        />
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || firstName.trim() === "" || lastName.trim() === ""}
            onClick={save}
          >
            {t("common.save", "Save")}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => setEditing(false)}
          >
            {t("common.cancel", "Cancel")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#367D7D] text-white font-bold">
        {getInitials(userProfile?.name)}
      </div>
      <div className="min-w-0">
        <p className="truncate font-medium text-grey-20 dark:text-white">{userProfile?.name}</p>
        <p className="truncate text-sm text-grey-50 dark:text-grey-70">
          {userProfile?.emailaddress}
        </p>
      </div>
      <button
        type="button"
        aria-label={t("settings.profileName.edit", "Edit name")}
        title={t("settings.profileName.edit", "Edit name")}
        className="ml-auto text-grey-40 hover:text-grey-20 dark:text-grey-80 dark:hover:text-white"
        onClick={startEditing}
      >
        <Pencil size={16} />
      </button>
    </div>
  );
};

export default ProfileNameEditor;
